import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth, homeFor } from '../context/AuthContext.jsx';
import PublicShell from '../components/PublicShell.jsx';
import CubMascot from '../components/CubMascot.jsx';
import { Loader, Alert } from '../components/ui.jsx';

/**
 * The leaderboard, on a page of its own.
 *
 * It used to be a section of the homepage, where it had to justify its
 * space against everything else on the way to signing up - which is why
 * it was cut to ten rows. Here it can show the whole board the endpoint
 * serves, and the prizes can be read rather than skimmed past.
 *
 * Still public, and still no sign-in: a child should be able to show
 * somebody where they are without handing over an account first.
 */

/*
 * What the top three win. Kept as data so the three cards cannot drift
 * apart, and so a change lands in one place.
 */
const PRIZES = [
  {
    place: 1,
    medal: '🥇',
    label: '1st place',
    cash: '₹7,000',
    extras: ['Summer internship', 'Certification'],
  },
  {
    place: 2,
    medal: '🥈',
    label: '2nd place',
    cash: '₹5,000',
    extras: ['Summer internship', 'Certification'],
  },
  {
    place: 3,
    medal: '🥉',
    label: '3rd place',
    cash: '₹2,500',
    extras: ['CyberCub T-shirt', 'Certification'],
  },
];

export default function Leaderboard() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Leaderboard · CyberCub';
    api
      .get('/public/leaderboard')
      .then(setBoard)
      .catch((err) => setError(err.message));
  }, []);

  const rows = board?.board ?? [];

  return (
    <PublicShell>
      <section className="about-head">
        <div className="about-head-text">
          <span className="home-eyebrow">Leaderboard</span>
          <h1>
            Every lesson finished
            <br />
            <span className="hl">puts you further up.</span>
          </h1>
          <p className="home-lede">
            Points come from finishing lessons and passing quizzes, so the board rewards getting
            through the course rather than replaying one quiz. Two cubs on the same score are
            separated by lessons done, and then by who got there first.
          </p>
        </div>
        <CubMascot avatar="cub-sun" mood="star" size={150} className="anim-float" />
      </section>

      <section className="home-section">
        {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

        {!board && !error ? (
          <Loader label="Counting the points…" />
        ) : (
          <div className="home-board">
            {rows.length === 0 ? (
              <p className="home-board-empty">
                🏁 Nobody has started yet — finish your first lesson and you will be top of this
                board.
              </p>
            ) : (
              <>
                <p className="home-lead lb-count">
                  {rows.length === board.total
                    ? `${rows.length} ${rows.length === 1 ? 'cub' : 'cubs'} on the board.`
                    : `The top ${rows.length}, out of ${board.total} on the board.`}
                </p>

                <ol className="lb-board">
                  {rows.map((r) => {
                    const medal =
                      r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : null;
                    return (
                      <li key={`${r.rank}-${r.name}`} className={`lb-row ${medal ? 'is-top' : ''}`}>
                        <span className="lb-rank">{medal ?? r.rank}</span>
                        <CubMascot avatar={r.avatar ?? 'cub-teal'} size={38} className="lb-face" />
                        <span className="lb-who">
                          <strong>{r.name}</strong>
                          <em>
                            {r.school ?? 'Learning at home'}
                            {r.city ? ` · ${r.city}` : ''}
                          </em>
                        </span>
                        <span className="lb-stats">
                          <b>{r.points.toLocaleString()}</b>
                          <i>points</i>
                        </span>
                        <span className="lb-lessons">{r.lessonsCompleted} lessons</span>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}

            <div className="lb-prizes">
              <h3>Prizes for the top three 🎁</h3>
              <div className="lb-prize-grid">
                {PRIZES.map((p) => (
                  <div key={p.place} className={`lb-prize lb-prize-${p.place}`}>
                    <span className="lb-prize-medal" aria-hidden="true">{p.medal}</span>
                    <strong>{p.label}</strong>
                    <span className="lb-prize-cash">{p.cash}</span>
                    <ul className="lb-prize-extras">
                      {p.extras.map((x) => <li key={x}>{x}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="lb-prize-note">
                🏫 Cash prizes are presented at the school&rsquo;s annual function.
              </p>
            </div>
          </div>
        )}
      </section>

      <section className="home-section home-band lb-close">
        <h2 className="home-h2">Not on it yet?</h2>
        <p className="home-sub">
          Points start with the first lesson. Nobody is too far behind to climb — the board counts
          what you finish, not when you started.
        </p>
        <div className="home-cta">
          {user ? (
            <Link to={homeFor(user.role)} className="btn btn-lg">Go to my courses →</Link>
          ) : (
            <>
              <Link to="/login" className="btn btn-lg">Log in</Link>
              <Link to="/#courses" className="btn btn-lg btn-ghost">See the courses</Link>
            </>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
